import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  DollarSign,
  History,
  Layers,
  Plus,
  Save,
  Trash2,
} from "lucide-react";

import { ApiError, formatCLP, formatDate } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  TableWrap,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarTarifa,
  crearNuevaBase,
  crearNuevosTramos,
  listarBases,
  listarTramos,
  obtenerTarifa,
  type NuevaVigenciaBasePayload,
  type NuevaVigenciaTramosPayload,
  type TarifaBaseRead,
  type TarifaDetailRead,
  type TarifaUpdatePayload,
  type TramoEnVigenciaInput,
  type TramoTarifaRead,
} from "./api";

export function TarifaDetailPage(): JSX.Element {
  const params = useParams<{ tarifaId: string }>();
  const tarifaId = Number(params.tarifaId);

  const detalleQ = useQuery({
    queryKey: ["tarifa", tarifaId],
    queryFn: () => obtenerTarifa(tarifaId),
    enabled: !Number.isNaN(tarifaId),
  });

  if (Number.isNaN(tarifaId)) {
    return <ErrorCarga mensaje="ID de tarifa inválido" />;
  }
  if (detalleQ.isPending) {
    return <Cargando />;
  }
  if (detalleQ.isError || !detalleQ.data) {
    return (
      <ErrorCarga
        error={detalleQ.error}
        mensaje="Error al cargar la tarifa."
        onReintentar={() => void detalleQ.refetch()}
      />
    );
  }

  const tarifa = detalleQ.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Tarifa ${tarifa.nombre}`}
        icon={DollarSign}
        subtitle={`Alcantarillado ${(tarifa.porcentaje_alcantarillado * 100).toFixed(1)}% del consumo`}
        volver={{ etiqueta: "Tarifas", ruta: "/tarifas" }}
      />

      <CabeceraSection tarifa={tarifa} />
      <BaseVigenteSection tarifa={tarifa} />
      <TramosVigentesSection tarifa={tarifa} />
      <HistoricoBasesSection tarifaId={tarifaId} />
      <HistoricoTramosSection tarifaId={tarifaId} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Cabecera
// ────────────────────────────────────────────────────────────────────────

function CabeceraSection({
  tarifa,
}: {
  tarifa: TarifaDetailRead;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [nombre, setNombre] = useState(tarifa.nombre);
  const [porcentaje, setPorcentaje] = useState(
    String(tarifa.porcentaje_alcantarillado),
  );
  const [error, setError] = useState<string | null>(null);

  const dirty =
    nombre !== tarifa.nombre ||
    Number(porcentaje) !== tarifa.porcentaje_alcantarillado;

  const mutation = useMutation({
    mutationFn: (p: TarifaUpdatePayload) => actualizarTarifa(tarifa.id, p),
    onSuccess: () => {
      toast.success("Cabecera actualizada");
      void qc.invalidateQueries({ queryKey: ["tarifa", tarifa.id] });
      void qc.invalidateQueries({ queryKey: ["tarifas"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  return (
    <section className="rounded-lg bg-white ring-1 ring-neutral-200 p-4 space-y-3">
      <header className="flex items-center gap-2">
        <DollarSign className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />
        <h2 className="text-base font-semibold text-neutral-900">
          Cabecera tarifaria
        </h2>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <Input
          label="Nombre"
          type="text"
          maxLength={50}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          disabled={mutation.isPending}
        />
        <Input
          label="% Alcantarillado (0–1)"
          type="number"
          step="0.01"
          min={0}
          max={1}
          value={porcentaje}
          onChange={(e) => setPorcentaje(e.target.value)}
          disabled={mutation.isPending}
        />
        <Button
          icon={Save}
          disabled={!dirty || mutation.isPending}
          onClick={() => {
            setError(null);
            const p = Number(porcentaje);
            if (!Number.isFinite(p) || p < 0 || p > 1)
              return setError("Porcentaje 0-1");
            mutation.mutate({
              nombre: nombre.trim(),
              porcentaje_alcantarillado: p,
            });
          }}
        >
          {mutation.isPending ? "Guardando…" : "Guardar cabecera"}
        </Button>
      </div>
      {error && <p className="text-sm text-rose-700">{error}</p>}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Base vigente + crear nueva
// ────────────────────────────────────────────────────────────────────────

function BaseVigenteSection({
  tarifa,
}: {
  tarifa: TarifaDetailRead;
}): JSX.Element {
  const [openForm, setOpenForm] = useState(false);
  const base = tarifa.base_vigente;

  return (
    <section className="rounded-lg bg-white ring-1 ring-neutral-200 p-4 space-y-3">
      <header className="flex items-center gap-2">
        <Layers className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />
        <h2 className="text-base font-semibold text-neutral-900">
          Cargos fijos vigentes
        </h2>
        <Button
          size="sm"
          icon={Plus}
          onClick={() => setOpenForm((v) => !v)}
          className="ml-auto"
        >
          Nueva vigencia
        </Button>
      </header>

      {openForm && (
        <NuevaBaseForm
          tarifaId={tarifa.id}
          base={base}
          onClose={() => setOpenForm(false)}
        />
      )}

      {base ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <Stat label="Cargo fijo agua" value={formatCLP(base.cargo_fijo_agua)} />
          <Stat label="Cargo alcantarillado" value={formatCLP(base.cargo_fijo_alc)} />
          <Stat label="Fondo reposición" value={formatCLP(base.fondo_reposicion)} />
          <Stat label="Otros cargos" value={formatCLP(base.otros_cargos)} />
          <Stat
            label="Vigente desde"
            value={formatDate(base.fecha_inicio_vigencia)}
            highlight
          />
          {base.descripcion && (
            <div className="col-span-full text-xs text-neutral-500 italic">
              {base.descripcion}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-amber-700">
          No hay cargos fijos vigentes — esta tarifa no se puede usar para
          facturar hasta cargar una vigencia.
        </p>
      )}
    </section>
  );
}

function NuevaBaseForm({
  tarifaId,
  base,
  onClose,
}: {
  tarifaId: number;
  base: TarifaBaseRead | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [fechaInicio, setFechaInicio] = useState("");
  const [agua, setAgua] = useState(String(base?.cargo_fijo_agua ?? 0));
  const [alc, setAlc] = useState(String(base?.cargo_fijo_alc ?? 0));
  const [fondo, setFondo] = useState(String(base?.fondo_reposicion ?? 0));
  const [otros, setOtros] = useState(String(base?.otros_cargos ?? 0));
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (p: NuevaVigenciaBasePayload) => crearNuevaBase(tarifaId, p),
    onSuccess: () => {
      toast.success("Nueva vigencia de cargos fijos cargada");
      void qc.invalidateQueries({ queryKey: ["tarifa", tarifaId] });
      void qc.invalidateQueries({ queryKey: ["tarifa-bases", tarifaId] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  function submit(): void {
    setError(null);
    if (!fechaInicio) return setError("Fecha requerida");
    const nums = [agua, alc, fondo, otros].map(Number);
    if (nums.some((n) => !Number.isFinite(n) || n < 0))
      return setError("Cargos deben ser ≥ 0");
    mutation.mutate({
      fecha_inicio_vigencia: fechaInicio,
      cargo_fijo_agua: Number(agua),
      cargo_fijo_alc: Number(alc),
      fondo_reposicion: Number(fondo),
      otros_cargos: Number(otros),
      descripcion: descripcion.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([fechaInicio, agua, alc, fondo, otros, descripcion]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mutation.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva vigencia de cargos fijos"
      icon={DollarSign}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={intentarCerrar}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Creando…" : "Cargar vigencia"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input
            label="Vigencia desde"
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            disabled={mutation.isPending}
            containerClassName="md:col-span-1"
          />
          <NumberInput
            label="Cargo agua"
            value={agua}
            onChange={setAgua}
            disabled={mutation.isPending}
          />
          <NumberInput
            label="Cargo alc."
            value={alc}
            onChange={setAlc}
            disabled={mutation.isPending}
          />
          <NumberInput
            label="Fondo rep."
            value={fondo}
            onChange={setFondo}
            disabled={mutation.isPending}
          />
          <NumberInput
            label="Otros"
            value={otros}
            onChange={setOtros}
            disabled={mutation.isPending}
          />
        </div>
        <Input
          label="Descripción (opcional)"
          type="text"
          maxLength={255}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="ej. Reajuste anual IPC 2026"
          disabled={mutation.isPending}
        />

        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Tramos vigentes + crear nuevo set
// ────────────────────────────────────────────────────────────────────────

function TramosVigentesSection({
  tarifa,
}: {
  tarifa: TarifaDetailRead;
}): JSX.Element {
  const [openForm, setOpenForm] = useState(false);

  return (
    <section className="rounded-lg bg-white ring-1 ring-neutral-200 p-4 space-y-3">
      <header className="flex items-center gap-2">
        <Layers className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />
        <h2 className="text-base font-semibold text-neutral-900">
          Tramos por consumo (vigentes)
        </h2>
        <Button
          size="sm"
          icon={Plus}
          onClick={() => setOpenForm((v) => !v)}
          className="ml-auto"
        >
          Nuevo set de tramos
        </Button>
      </header>

      {openForm && (
        <NuevoTramosForm
          tarifaId={tarifa.id}
          existentes={tarifa.tramos_vigentes}
          onClose={() => setOpenForm(false)}
        />
      )}

      {tarifa.tramos_vigentes.length === 0 ? (
        <p className="text-sm text-amber-700">
          No hay tramos vigentes — la tarifa no calcula consumo hasta cargar al
          menos un set.
        </p>
      ) : (
        <TableWrap label="Tramos vigentes">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Desde m³</th>
                <th scope="col" className="px-3 py-2 text-left">Hasta m³</th>
                <th scope="col" className="px-3 py-2 text-right">Valor / m³</th>
                <th scope="col" className="px-3 py-2 text-left">Vigente desde</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {tarifa.tramos_vigentes.map((t) => (
                <tr key={t.id}>
                  <td className="px-3 py-2">{t.desde_m3}</td>
                  <td className="px-3 py-2">
                    {t.hasta_m3 === null ? (
                      <span className="italic text-neutral-500">∞ (abierto)</span>
                    ) : (
                      t.hasta_m3
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">
                    {formatCLP(t.valor_unitario)}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {formatDate(t.fecha_inicio_vigencia)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}

interface TramoDraft {
  desde_m3: string;
  hasta_m3: string;
  valor_unitario: string;
}

function emptyDraft(desde = 0): TramoDraft {
  return { desde_m3: String(desde), hasta_m3: "", valor_unitario: "" };
}

function NuevoTramosForm({
  tarifaId,
  existentes,
  onClose,
}: {
  tarifaId: number;
  existentes: TramoTarifaRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [fechaInicio, setFechaInicio] = useState("");
  const [drafts, setDrafts] = useState<TramoDraft[]>(() => {
    if (existentes.length > 0) {
      return existentes.map((t) => ({
        desde_m3: String(t.desde_m3),
        hasta_m3: t.hasta_m3 === null ? "" : String(t.hasta_m3),
        valor_unitario: String(t.valor_unitario),
      }));
    }
    return [emptyDraft(0)];
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (p: NuevaVigenciaTramosPayload) =>
      crearNuevosTramos(tarifaId, p),
    onSuccess: (tramos) => {
      toast.success(`${tramos.length} tramos cargados`);
      void qc.invalidateQueries({ queryKey: ["tarifa", tarifaId] });
      void qc.invalidateQueries({ queryKey: ["tarifa-tramos", tarifaId] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  function setDraft(i: number, patch: Partial<TramoDraft>): void {
    setDrafts((ds) => ds.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }
  function agregar(): void {
    const ult = drafts[drafts.length - 1];
    const proxDesde =
      ult && ult.hasta_m3 ? Number(ult.hasta_m3) + 1 : 0;
    setDrafts((ds) => [...ds, emptyDraft(proxDesde)]);
  }
  function quitar(i: number): void {
    setDrafts((ds) => ds.filter((_, idx) => idx !== i));
  }

  function submit(): void {
    setError(null);
    if (!fechaInicio) return setError("Fecha requerida");
    const tramos: TramoEnVigenciaInput[] = [];
    for (const [i, d] of drafts.entries()) {
      const desde = Number(d.desde_m3);
      const valor = Number(d.valor_unitario);
      if (!Number.isFinite(desde) || desde < 0)
        return setError(`Tramo ${i + 1}: desde inválido`);
      if (!Number.isFinite(valor) || valor < 0)
        return setError(`Tramo ${i + 1}: valor inválido`);
      const hasta = d.hasta_m3.trim() === "" ? null : Number(d.hasta_m3);
      if (hasta !== null && (!Number.isFinite(hasta) || hasta < desde))
        return setError(`Tramo ${i + 1}: hasta inválido`);
      tramos.push({
        desde_m3: desde,
        hasta_m3: hasta,
        valor_unitario: valor,
      });
    }
    mutation.mutate({ fecha_inicio_vigencia: fechaInicio, tramos });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([fechaInicio, drafts]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mutation.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nuevo set de tramos"
      icon={Layers}
      size="xl"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={intentarCerrar}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Cargando…" : "Cargar set"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <Input
            label="Vigencia desde"
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            disabled={mutation.isPending}
          />
          <div className="md:col-span-2 text-xs text-neutral-500 self-end">
            Reglas: el primer tramo arranca en 0, son contiguos (cada{" "}
            <code>desde</code> = anterior <code>hasta</code> + 1), sólo el último
            puede dejar <code>hasta</code> vacío (tramo abierto).
          </div>
        </div>

        <div className="space-y-2">
          {drafts.map((d, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-2 items-end rounded-md bg-white p-2 border border-neutral-200"
            >
              <Input
                label="Desde m³"
                type="number"
                min={0}
                value={d.desde_m3}
                onChange={(e) => setDraft(i, { desde_m3: e.target.value })}
                disabled={mutation.isPending}
                containerClassName="col-span-3"
              />
              <Input
                label={`Hasta m³ ${
                  i === drafts.length - 1 ? "(vacío = abierto)" : ""
                }`}
                type="number"
                min={0}
                value={d.hasta_m3}
                onChange={(e) => setDraft(i, { hasta_m3: e.target.value })}
                disabled={mutation.isPending}
                containerClassName="col-span-3"
              />
              <Input
                label="Valor por m³ (CLP)"
                type="number"
                min={0}
                value={d.valor_unitario}
                onChange={(e) => setDraft(i, { valor_unitario: e.target.value })}
                disabled={mutation.isPending}
                containerClassName="col-span-4"
              />
              <div className="col-span-2 flex justify-end">
                {drafts.length > 1 && (
                  <Button
                    variant="linkDanger"
                    size="sm"
                    icon={Trash2}
                    onClick={() => quitar(i)}
                    disabled={mutation.isPending}
                  >
                    Quitar
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            icon={Plus}
            onClick={agregar}
            disabled={mutation.isPending}
          >
            Agregar tramo
          </Button>
        </div>

        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Históricos
// ────────────────────────────────────────────────────────────────────────

function HistoricoBasesSection({
  tarifaId,
}: {
  tarifaId: number;
}): JSX.Element {
  const q = useQuery({
    queryKey: ["tarifa-bases", tarifaId],
    queryFn: () => listarBases(tarifaId),
  });

  return (
    <section className="rounded-lg bg-white ring-1 ring-neutral-200 p-4 space-y-3">
      <header className="flex items-center gap-2">
        <History className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />
        <h2 className="text-base font-semibold text-neutral-900">
          Histórico de cargos fijos
        </h2>
      </header>
      {q.isPending && <Cargando inline />}
      {q.data && q.data.length === 0 && (
        <p className="text-sm text-neutral-500">Sin historial.</p>
      )}
      {q.data && q.data.length > 0 && (
        <TableWrap label="Histórico de cargos fijos">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Vigencia</th>
                <th scope="col" className="px-3 py-2 text-right">Agua</th>
                <th scope="col" className="px-3 py-2 text-right">Alc.</th>
                <th scope="col" className="px-3 py-2 text-right">Fondo</th>
                <th scope="col" className="px-3 py-2 text-right">Otros</th>
                <th scope="col" className="px-3 py-2 text-right">Total</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {q.data.map((b) => (
                <tr key={b.id} className={b.estado === "vigente" ? "" : "opacity-70"}>
                  <td className="px-3 py-2">
                    <div>{formatDate(b.fecha_inicio_vigencia)}</div>
                    <div className="text-xs text-neutral-500">
                      →{" "}
                      {b.fecha_fin_vigencia
                        ? formatDate(b.fecha_fin_vigencia)
                        : "vigente"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {formatCLP(b.cargo_fijo_agua)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {formatCLP(b.cargo_fijo_alc)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {formatCLP(b.fondo_reposicion)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {formatCLP(b.otros_cargos)}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">
                    {formatCLP(
                      b.cargo_fijo_agua +
                        b.cargo_fijo_alc +
                        b.fondo_reposicion +
                        b.otros_cargos,
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <EstadoVigBadge estado={b.estado} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}

function HistoricoTramosSection({
  tarifaId,
}: {
  tarifaId: number;
}): JSX.Element {
  const q = useQuery({
    queryKey: ["tarifa-tramos", tarifaId],
    queryFn: () => listarTramos(tarifaId),
  });

  const grupos = useMemo(() => {
    const m = new Map<string, TramoTarifaRead[]>();
    for (const t of q.data ?? []) {
      const k = t.fecha_inicio_vigencia;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return Array.from(m.entries()).sort(
      ([a], [b]) => (a < b ? 1 : -1),
    );
  }, [q.data]);

  return (
    <section className="rounded-lg bg-white ring-1 ring-neutral-200 p-4 space-y-3">
      <header className="flex items-center gap-2">
        <History className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />
        <h2 className="text-base font-semibold text-neutral-900">
          Histórico de tramos
        </h2>
      </header>
      {q.isPending && <Cargando inline />}
      {grupos.length === 0 && !q.isPending && (
        <p className="text-sm text-neutral-500">Sin historial.</p>
      )}
      {grupos.map(([fecha, tramos]) => {
        const estado = tramos[0]?.estado ?? "historico";
        const fin = tramos[0]?.fecha_fin_vigencia ?? null;
        return (
          <div
            key={fecha}
            className={`rounded-md border border-neutral-200 p-3 ${
              estado === "vigente" ? "" : "opacity-70 bg-neutral-50"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-neutral-700">
                Vigencia desde {formatDate(fecha)}
              </span>
              {fin && (
                <span className="text-xs text-neutral-500">
                  → {formatDate(fin)}
                </span>
              )}
              <EstadoVigBadge estado={estado} />
            </div>
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th scope="col" className="text-left">Desde</th>
                  <th scope="col" className="text-left">Hasta</th>
                  <th scope="col" className="text-right">Valor / m³</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {tramos
                  .slice()
                  .sort((a, b) => a.desde_m3 - b.desde_m3)
                  .map((t) => (
                    <tr key={t.id}>
                      <td className="py-1">{t.desde_m3}</td>
                      <td className="py-1">
                        {t.hasta_m3 === null ? "∞" : t.hasta_m3}
                      </td>
                      <td className="py-1 text-right">
                        {formatCLP(t.valor_unitario)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Auxiliares UI
// ────────────────────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded-md border p-2 ${
        highlight
          ? "border-emerald-100 bg-emerald-50"
          : "border-neutral-100 bg-neutral-50"
      }`}
    >
      <div className="text-xs uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div
        className={`mt-1 text-sm font-semibold ${
          highlight ? "text-emerald-900" : "text-neutral-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <Input
      label={label}
      type="number"
      min={0}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    />
  );
}

function EstadoVigBadge({
  estado,
}: {
  estado: "vigente" | "historico";
}): JSX.Element {
  return estado === "vigente" ? (
    <Badge tone="emerald">vigente</Badge>
  ) : (
    <Badge tone="slate">histórica</Badge>
  );
}
